import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import PieChart from './PieChart';
import GaugeChart from './GaugeChart';
import { RightArrowSvg } from '../svgs/index';
import { InventoryGraphicsData, InventoryGraphicsSpecimenTypeQuantity } from '../../types/interfaces';
import { StoragePlan } from '../../types/interfaces/account.interfaces';
import { Animal } from '../../types/interfaces/animal.interfaces';
import { SpecimenType } from '../../types/interfaces/specimen.interfaces';
import { StoragePlanUOM } from '../../types/enums/storagePlanUOM.enum';
import { InventoryStorageType } from '../../types/enums/storageType.enum';
import { activeContexts } from '../../types/enums/activeContexts.enum';
import { getAccountAnimals } from '../../api/animalsApi';
import { getCountByAnimal, getCountBySpecimenType, getInventoryCount } from '../../api/inventoryApi';
import { getSpecimenTypes } from '../../api/specimensTypesApi';
import { getStoragePlans } from '../../api/storagePlansApi';
import { getOnFarmInventoryGraphicsData } from '../../services/onFarmInventory.service';
import { centsToDollars } from '../../services/account.service';
import { showToast } from '../../services/toast.service';
import { toastMessages } from '../../constants/errorMessages';
import { REACT_APP_ON_FARM_ENABLED } from '../../constants/apiEndpoints';
import { LABEL_CONSTANTS } from '../../constants/common';
import './inventoryGraphics.scss';

interface StoragePlanRange extends StoragePlan {
  lower: number;
  upper?: number;
}

const inventoryStorageOptions = [
  { type: InventoryStorageType.Rsg, label: 'RSG Storage' },
  ...(REACT_APP_ON_FARM_ENABLED ? [{ type: InventoryStorageType.OnFarm, label: 'On Farm Storage' }] : []),
];

const InventoryGraphics: FC<{ accountId: number; activeContext?: activeContexts }> = ({
  accountId,
  activeContext = activeContexts.account,
}): JSX.Element => {
  const [storageType, setStorageType] = useState<InventoryStorageType>(InventoryStorageType.Rsg);
  const [totalInventory, setTotalInventory] = useState<number>();
  const [storagePlan, setStoragePlan] = useState<StoragePlanRange | null>(null);
  const [storagePlanOptions, setStoragePlanOptions] = useState<StoragePlanRange[]>([]);
  const [typeInventory, setTypeInventory] = useState<InventoryGraphicsSpecimenTypeQuantity[]>([]);
  const [animalInventory, setAnimalInventory] = useState<{ quantity: number; animal: Animal }[]>([]);
  const [loadingTotal, setLoadingTotal] = useState<boolean>(true);
  const [loadingPlan, setLoadingPlan] = useState<boolean>(true);
  const [loadingPlanOptions, setLoadingPlanOptions] = useState<boolean>(true);
  const [loadingTypes, setLoadingTypes] = useState<boolean>(true);
  const [loadingAnimals, setLoadingAnimals] = useState<boolean>(true);
  const [onFarmInventoryData, setOnFarmInventoryData] = useState<InventoryGraphicsData>({
    totalInventory: 0,
    typeInventory: [],
    animalInventory: [],
  });
  const [loadingOnFarm, setLoadingOnFarm] = useState<boolean>(true);
  const [slideIndex, setSlideIndex] = useState<number>(1);

  const handleGetTotalInventory = useCallback(async () => {
    if (accountId) {
      try {
        const { data: total } = await getInventoryCount({ accountId: accountId });
        setTotalInventory(total);
      } catch (error: any) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setLoadingTotal(false);
      }
    }
  }, [accountId]);

  const handleGetSpecimenTypes = useCallback(async () => {
    if (accountId) {
      try {
        const { data: types } = await getSpecimenTypes();
        const inventoriesByType = await Promise.all(
          types.map(async (specimenType: SpecimenType): Promise<InventoryGraphicsSpecimenTypeQuantity> => {
            const { data: quantity } = await getCountBySpecimenType(specimenType.specimenTypeId, { accountId: accountId });
            return {
              typeName: specimenType.name,
              typeId: specimenType.specimenTypeId,
              quantity: quantity,
            };
          }),
        );
        setTypeInventory(inventoriesByType);
      } catch (error: any) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setLoadingTypes(false);
      }
    }
  }, [accountId]);

  const handleGetAnimals = useCallback(async () => {
    if (accountId) {
      try {
        const response = await getAccountAnimals(accountId);
        const animals = response.data as Animal[];
        const animalQuantities = await Promise.all(
          animals.map(async (animal: Animal): Promise<{ quantity: number; animal: Animal }> => {
            const { data: quantity } = await getCountByAnimal(animal.animalId!, { accountId: accountId });
            return { quantity: quantity, animal: animal };
          }),
        );

        setAnimalInventory(animalQuantities);
      } catch (error: any) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setLoadingAnimals(false);
      }
    }
  }, [accountId]);

  const handleGetOnFarmInventory = useCallback(async () => {
    if (accountId) {
      try {
        const data = await getOnFarmInventoryGraphicsData(accountId, activeContext);
        setOnFarmInventoryData(data);
      } catch (error: any) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setLoadingOnFarm(false);
      }
    }
  }, [accountId, activeContext]);

  const selectedInventoryData = useMemo<InventoryGraphicsData>(() => {
    if (storageType === InventoryStorageType.OnFarm) {
      return onFarmInventoryData;
    }
    return {
      totalInventory: totalInventory ?? 0,
      typeInventory,
      animalInventory,
    };
  }, [storageType, totalInventory, typeInventory, animalInventory, onFarmInventoryData]);

  const getInventoryGraphs = (className: string, width = 128, height = 128) => {
    return (
      <div className={`charts ${className}`}>
        <div className={getInventoryChartClassName(className, 1)} data-testid={`slide-1-${className}`}>
          <h3>{isOnFarmSelected ? LABEL_CONSTANTS.ON_FARM_INVENTORY : LABEL_CONSTANTS.STORAGE_BREAKDOWN}</h3>
          <div className="chart">
            {isOnFarmSelected ? (
              <div className="gauge-chart on-farm-gauge-chart" style={{ width, height }}>
                <div className="outer-edge">
                  <div className="inner-edge">
                    <div className="value" data-testid="inventory-value">
                      {loadingOnFarm ? '--' : selectedInventoryData.totalInventory}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <GaugeChart
                width={width}
                height={height}
                data={storagePlanOptions.map(plan => ({
                  lower: plan.lower,
                  upper: plan.upper,
                  label: plan.name.split(' ')[0],
                }))}
                value={selectedInventoryData.totalInventory}
                loading={loadingPlan || loadingPlanOptions || currentTotalInventoryLoading}
              />
            )}
          </div>
        </div>
        <div className={getInventoryChartClassName(className, 2)} data-testid={`slide-2-${className}`}>
          <h3>{LABEL_CONSTANTS.ANIMAL_BREAKDOWN}</h3>
          <div className="chart">
            <PieChart
              width={width}
              height={height}
              data={selectedInventoryData.animalInventory.map(animal => {
                const percent = selectedInventoryData.totalInventory
                  ? (animal.quantity / selectedInventoryData.totalInventory) * 100
                  : 0;
                return {
                  percentage: percent,
                  message: `${animal.animal.name} makes up ${percent.toFixed(1)}% of your inventory`,
                };
              })}
              loading={isOnFarmSelected ? loadingOnFarm : loadingAnimals || currentTotalInventoryLoading}
            />
          </div>
        </div>
        <div className={getInventoryChartClassName(className, 3)} data-testid={`slide-3-${className}`}>
          <h3>{LABEL_CONSTANTS.INVENTORY_TYPE}</h3>
          <div className="chart">
            <PieChart
              width={width}
              height={height}
              data={selectedInventoryData.typeInventory.map((type: InventoryGraphicsSpecimenTypeQuantity) => {
                const percent = selectedInventoryData.totalInventory
                  ? (type.quantity / selectedInventoryData.totalInventory) * 100
                  : 0;
                return {
                  percentage: percent,
                  message: `${type.typeName} Items make up ${percent.toFixed(1)}% of your inventory`,
                };
              })}
              loading={isOnFarmSelected ? loadingOnFarm : loadingTypes || currentTotalInventoryLoading}
            />
          </div>
        </div>
        <button className="right-arrow mobile-view" data-testid={`right-arrow-button-${className}`} onClick={nextSlide}>
          <RightArrowSvg />
        </button>
      </div>
    );
  };

  const isOnFarmSelected = storageType === InventoryStorageType.OnFarm;
  const currentTotalInventoryLoading = isOnFarmSelected ? false : loadingTotal;

  const getInventoryChartClassName = (className: string, index: number) => {
    return className === 'mobile-view' ? `chart-container ${slideIndex == index ? 'active-slide' : 'slide'}` : 'chart-container';
  };

  const nextSlide = () => {
    if (slideIndex !== 3) {
      setSlideIndex(slideIndex + 1);
    } else if (slideIndex === 3) {
      setSlideIndex(1);
    }
  };

  const showStorageTier = storageType === InventoryStorageType.Rsg;

  const renderHeaderDetails = () => {
    if (!showStorageTier || !storagePlan) {
      return null;
    }
    const { costInCents, unitOfMeasurement, period, name } = storagePlan;

    return (
      <div className="header-details">
        <h2>{LABEL_CONSTANTS.STORAGE_TIER}</h2>
        <p>
          ${centsToDollars(costInCents)}
          {unitOfMeasurement === StoragePlanUOM.UNIT && '/Unit'}/{period}
        </p>
        <p>
          {period}ly Storage: {name}
        </p>
      </div>
    );
  };

  useEffect(() => {
    if (totalInventory !== undefined) {
      setStoragePlan(
        storagePlanOptions.reduce((acc: StoragePlanRange | null, plan: StoragePlanRange) => {
          return plan.lower <= totalInventory && (plan.upper === undefined || totalInventory <= plan.upper) ? plan : acc;
        }, null),
      );
      setLoadingPlan(false);
    }
  }, [totalInventory, storagePlanOptions]);

  const renderStorageTypeButtons = () => {
    if (!REACT_APP_ON_FARM_ENABLED) {
      return null;
    }

    return (
      <div className="storage-type-toggle">
        {inventoryStorageOptions.map(option => (
          <button
            key={option.type}
            type="button"
            className={`storage-type-button ${storageType === option.type ? 'active' : ''}`}
            onClick={() => setStorageType(option.type)}>
            {option.label}
          </button>
        ))}
      </div>
    );
  };

  const handleGetStoragePlans = useCallback(async () => {
    try {
      const { data: plans } = await getStoragePlans();
      const planRanges: StoragePlanRange[] = plans.map((plan: StoragePlan) => {
        const numbers = plan.name.split('-');
        const lower = +numbers[0].replace(/[^\d]+/, '');
        const upper = numbers.length > 1 ? +numbers[1].replace(/[^\d]+/, '') : undefined;
        const planRange = { ...plan, lower: lower, upper: upper };
        return planRange;
      });
      setStoragePlanOptions(planRanges);
    } catch (error: any) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setLoadingPlanOptions(false);
    }
  }, []);

  useEffect(() => {
    handleGetTotalInventory();
  }, [handleGetTotalInventory]);

  useEffect(() => {
    handleGetSpecimenTypes();
  }, [handleGetSpecimenTypes]);

  useEffect(() => {
    handleGetAnimals();
  }, [handleGetAnimals]);

  useEffect(() => {
    if (REACT_APP_ON_FARM_ENABLED) {
      handleGetOnFarmInventory();
    } else {
      setLoadingOnFarm(false);
    }
  }, [handleGetOnFarmInventory]);

  useEffect(() => {
    handleGetStoragePlans();
  }, [handleGetStoragePlans]);

  return (
    <div className="inventory-graphics">
      <div className="header">
        {renderHeaderDetails()}
        {renderStorageTypeButtons()}
      </div>
      <hr />

      {getInventoryGraphs('desktop-view')}
      {getInventoryGraphs('tablet-view', 120, 120)}
      <div className="charts-container mobile-view">
        {getInventoryGraphs('mobile-view', 160, 160)}
        <div className="dots-container">
          {Array.from({ length: 3 }).map((item, index) => (
            <div
              key={`dot ${index}`}
              onClick={() => setSlideIndex(index + 1)}
              className={slideIndex === index + 1 ? 'dot active' : 'dot'}
              data-testid={`slide-dot-${index + 1}`}></div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InventoryGraphics;
